import { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Space, Typography, InputNumber, Input, Button, Tooltip, Select } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ColorPickerWithLabel } from "../OverviewSettings/ColorPickerWithLabel";
import { useSelector } from "react-redux";
import { PermissionOverlay } from "../../PermissionedComponents";

const { Option } = Select;

export const DifficultyBarConfig = ({ onSave }) => {
  const barRef = useRef(null);

  const {
    info: { difficulty_bar_config = {} }
  } = useSelector(({ team }) => team);

  // Default configuration
  const defaultConfig = {
    colors: ["#7CFC00", "#32CD32", "#FFD700", "#FF8C00", "#FF0000"],
    segment_names: ["Easy", "Light", "Moderate", "Hard", "Very Hard"],
    segments: 5,
    thresholds: [0.20, 0.35, 0.50, 0.65]
  };

  const [config, setConfig] = useState({ ...defaultConfig, ...difficulty_bar_config });
  const [errors, setErrors] = useState({});
  const [dragState, setDragState] = useState({ isDragging: false, thresholdIndex: -1, startX: 0 });

  useEffect(() => {
    setConfig({ ...defaultConfig, ...difficulty_bar_config });
  }, [difficulty_bar_config]);

  const validateConfig = (newConfig) => {
    const validationErrors = {};

    // Validate segments (2-5 inclusive)
    if (newConfig.segments < 2 || newConfig.segments > 5) {
      validationErrors.segments = "Number of segments must be between 2-5";
    }

    // Validate colors match segments
    if (newConfig.colors.length !== newConfig.segments) {
      validationErrors.colors = "Number of colors must match number of segments";
    }

    // Validate thresholds = segments - 1
    if (newConfig.thresholds.length !== newConfig.segments - 1) {
      validationErrors.thresholds = "Number of thresholds must equal (segments - 1)";
    }

    // Validate segment names match segments
    if (newConfig.segment_names.length !== newConfig.segments) {
      validationErrors.segment_names = "Number of segment names must equal number of segments";
    }

    // Validate thresholds are in ascending order and between 0 and 1
    for (let i = 0; i < newConfig.thresholds.length; i++) {
      const threshold = newConfig.thresholds[i];
      if (threshold < 0 || threshold > 1) {
        validationErrors.thresholds = "Thresholds must be between 0 and 1";
        break;
      }
      if (i > 0 && threshold <= newConfig.thresholds[i - 1]) {
        validationErrors.thresholds = "Thresholds must be in ascending order";
        break;
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  // Drag functionality
  const handleThresholdMouseDown = (e, thresholdIndex) => {
    e.preventDefault();
    setDragState({
      isDragging: true,
      thresholdIndex,
      startX: e.clientX
    });
  };

  const handleMouseMove = (e) => {
    if (!dragState.isDragging || !barRef.current) return;

    const rect = barRef.current.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, relativeX / rect.width));

    // Calculate constraints
    const thresholdIndex = dragState.thresholdIndex;
    const minValue = thresholdIndex === 0 ? 0.01 : config.thresholds[thresholdIndex - 1] + 0.01;
    const maxValue = thresholdIndex === config.thresholds.length - 1 ? 0.99 : config.thresholds[thresholdIndex + 1] - 0.01;

    // Constrain the value
    const constrainedValue = Math.max(minValue, Math.min(maxValue, percentage));

    // Update the threshold
    const newThresholds = [...config.thresholds];
    newThresholds[thresholdIndex] = constrainedValue;
    const newConfig = { ...config, thresholds: newThresholds };

    setConfig(newConfig);
    validateConfig(newConfig);
  };

  const handleMouseUp = () => {
    setDragState({ isDragging: false, thresholdIndex: -1, startX: 0 });
  };

  // Add global mouse event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (!dragState.isDragging || !barRef.current) return;

      const rect = barRef.current.getBoundingClientRect();
      const relativeX = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, relativeX / rect.width));

      // Calculate constraints
      const thresholdIndex = dragState.thresholdIndex;
      const minValue = thresholdIndex === 0 ? 0.01 : config.thresholds[thresholdIndex - 1] + 0.01;
      const maxValue = thresholdIndex === config.thresholds.length - 1 ? 0.99 : config.thresholds[thresholdIndex + 1] - 0.01;

      // Constrain the value
      const constrainedValue = Math.max(minValue, Math.min(maxValue, percentage));

      // Update the threshold
      const newThresholds = [...config.thresholds];
      newThresholds[thresholdIndex] = constrainedValue;
      const newConfig = { ...config, thresholds: newThresholds };

      setConfig(newConfig);
      validateConfig(newConfig);
    };

    if (dragState.isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Prevent text selection during drag
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [dragState.isDragging, config.thresholds, dragState.thresholdIndex]);

  const handleSegmentsChange = (segments) => {
    const newConfig = { ...config, segments };

    // Adjust arrays to match new segment count
    const newColors = [...newConfig.colors];
    const newSegmentNames = [...newConfig.segment_names];
    const newThresholds = [...newConfig.thresholds];

    if (segments > newColors.length) {
      // Add default colors for new segments, cycling through defaultConfig colors
      while (newColors.length < segments) {
        const colorIndex = newColors.length % defaultConfig.colors.length;
        newColors.push(defaultConfig.colors[colorIndex]);
      }
    } else {
      // Remove excess colors
      newColors.splice(segments);
    }

    if (segments > newSegmentNames.length) {
      // Add default names for new segments, cycling through defaultConfig names
      while (newSegmentNames.length < segments) {
        const nameIndex = newSegmentNames.length % defaultConfig.segment_names.length;
        newSegmentNames.push(defaultConfig.segment_names[nameIndex]);
      }
    } else {
      // Remove excess names
      newSegmentNames.splice(segments);
    }

    if (segments - 1 > newThresholds.length) {
      // Add default thresholds
      while (newThresholds.length < segments - 1) {
        const nextThreshold = newThresholds.length === 0 ? 0.2 : newThresholds[newThresholds.length - 1] + 0.15;
        newThresholds.push(Math.min(nextThreshold, 0.95));
      }
    } else {
      // Remove excess thresholds
      newThresholds.splice(segments - 1);
    }

    const updatedConfig = {
      ...newConfig,
      colors: newColors,
      segment_names: newSegmentNames,
      thresholds: newThresholds
    };

    setConfig(updatedConfig);
    validateConfig(updatedConfig);
  };

  const handleColorChange = (index, color) => {
    const newColors = [...config.colors];
    newColors[index] = color;
    const newConfig = { ...config, colors: newColors };
    setConfig(newConfig);
    validateConfig(newConfig);
  };

  const handleSegmentNameChange = (index, name) => {
    const newNames = [...config.segment_names];
    newNames[index] = name;
    const newConfig = { ...config, segment_names: newNames };
    setConfig(newConfig);
    validateConfig(newConfig);
  };

  const handleThresholdChange = (index, value) => {
    const newThresholds = [...config.thresholds];
    newThresholds[index] = value;
    const newConfig = { ...config, thresholds: newThresholds };
    setConfig(newConfig);
    validateConfig(newConfig);
  };

  const handleSave = async () => {
    if (validateConfig(config) && onSave) {
      await onSave(config);
    }
  };

  const handleReset = () => {
    setConfig(defaultConfig);
    validateConfig(defaultConfig);
  };

  const hasErrors = Object.keys(errors).length > 0;

  // Calculate segment widths based on thresholds
  const getSegmentWidths = () => {
    const widths = [];
    let previousThreshold = 0;

    for (let i = 0; i < config.segments; i++) {
      let currentThreshold;
      if (i === config.segments - 1) {
        // Last segment goes to 1.0
        currentThreshold = 1;
      } else {
        currentThreshold = config.thresholds[i] || 0;
      }

      const width = (currentThreshold - previousThreshold) * 100;
      widths.push(width);
      previousThreshold = currentThreshold;
    }

    return widths;
  };

  const segmentWidths = getSegmentWidths();

  // Calculate cumulative positions for threshold markers
  const getThresholdPositions = () => {
    const positions = [];
    let cumulativeWidth = 0;

    for (let i = 0; i < config.segments - 1; i++) {
      cumulativeWidth += segmentWidths[i];
      positions.push(cumulativeWidth);
    }

    return positions;
  };

  const thresholdPositions = getThresholdPositions();

  return (
    <Card title="Difficulty Bar Configuration" style={{ marginTop: 24 }}>
      <PermissionOverlay
        permissionCheck="EditDifficultyBar"
        tooltipMessage="You don't have permission to edit difficulty bar configuration"
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Preview Section */}
          <div>
            <Typography.Text strong>Preview:</Typography.Text>
            <div style={{
              marginTop: 8,
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              overflow: "hidden",
              height: "60px",
              display: "flex",
              alignItems: "stretch",
              position: "relative",
              cursor: dragState.isDragging ? "col-resize" : "default"
            }}
              onMouseMove={handleMouseMove}
              ref={barRef}
            >
              {config.colors.slice(0, config.segments).map((color, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: color,
                    width: `${segmentWidths[index]}%`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #fff",
                    borderLeft: index === 0 ? "none" : "1px solid #fff",
                    borderRight: index === config.segments - 1 ? "none" : "1px solid #fff",
                    position: "relative",
                    minHeight: "60px"
                  }}
                >
                  <Typography.Text
                    style={{
                      color: "#fff",
                      fontWeight: "bold",
                      textShadow: "1px 1px 2px rgba(0,0,0,0.7)",
                      fontSize: "12px",
                      textAlign: "center",
                      wordBreak: "break-word",
                      padding: "2px"
                    }}
                  >
                    {config.segment_names[index] || `Segment ${index + 1}`}
                  </Typography.Text>
                </div>
              ))}

              {/* Threshold markers positioned absolutely */}
              {config.thresholds.map((threshold, index) => (
                <div
                  key={`threshold-${index}`}
                  style={{
                    position: "absolute",
                    left: `${thresholdPositions[index]}%`,
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: "#fff",
                    border: "2px solid #1890ff",
                    borderRadius: "4px",
                    padding: "4px 6px",
                    fontSize: "10px",
                    fontWeight: "bold",
                    color: "#1890ff",
                    zIndex: 10,
                    cursor: "col-resize",
                    userSelect: "none",
                    minWidth: "32px",
                    textAlign: "center",
                    boxShadow: dragState.isDragging && dragState.thresholdIndex === index
                      ? "0 4px 8px rgba(24, 144, 255, 0.3)"
                      : "0 2px 4px rgba(0,0,0,0.1)",
                    transform: dragState.isDragging && dragState.thresholdIndex === index
                      ? "translate(-50%, -50%) scale(1.1)"
                      : "translate(-50%, -50%)",
                    transition: dragState.isDragging ? "none" : "all 0.2s ease"
                  }}
                  onMouseDown={(e) => handleThresholdMouseDown(e, index)}
                  title={`Drag to adjust threshold ${index + 1}`}
                >
                  {(threshold * 100).toFixed(0)}%
                </div>
              ))}
            </div>
            <div style={{ marginTop: 4, fontSize: "12px", color: "#666" }}>
              <Typography.Text type="secondary">
                Each segment represents a difficulty level from 0% to 100%. Drag the blue threshold markers to adjust segment boundaries.
              </Typography.Text>
            </div>
          </div>

          {/* Number of Segments */}
          <Row gutter={16} align="middle">
            <Col span={8}>
              <Typography.Text strong>Segments:</Typography.Text>
            </Col>
            <Col span={8}>
              <Select
                value={config.segments}
                onChange={handleSegmentsChange}
                style={{ width: "100%" }}
              >
                <Option value={2}>2</Option>
                <Option value={3}>3</Option>
                <Option value={4}>4</Option>
                <Option value={5}>5</Option>
              </Select>
            </Col>
            {errors.segments && (
              <Col span={8}>
                <Typography.Text type="danger">{errors.segments}</Typography.Text>
              </Col>
            )}
          </Row>

          {/* Colors Configuration */}
          <div>
            <Typography.Text strong>Colors:</Typography.Text>
            {errors.colors && <Typography.Text type="danger" style={{ marginLeft: 8 }}>{errors.colors}</Typography.Text>}
            <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
              {config.colors.map((color, index) => (
                <Col key={index} span={8}>
                  <Space>
                    <Typography.Text>{index + 1}:</Typography.Text>
                    <ColorPickerWithLabel
                      pickerId={`difficultyColor${index}`}
                      defaultColor={color}
                      handleColorChangeParent={(_, newColor) => handleColorChange(index, newColor)}
                    />
                  </Space>
                </Col>
              ))}
            </Row>
          </div>

          {/* Segment Names */}
          <div>
            <Typography.Text strong>Segment Names:</Typography.Text>
            {errors.segment_names && <Typography.Text type="danger" style={{ marginLeft: 8 }}>{errors.segment_names}</Typography.Text>}
            <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
              {config.segment_names.map((name, index) => (
                <Col key={index} span={8}>
                  <Input
                    placeholder={`Segment ${index + 1} name`}
                    value={name}
                    onChange={(e) => handleSegmentNameChange(index, e.target.value)}
                    addonBefore={`${index + 1}:`}
                  />
                </Col>
              ))}
            </Row>
          </div>

          {/* Thresholds */}
          <div>
            <Typography.Text strong>Thresholds:</Typography.Text>
            <Tooltip title="Thresholds define the boundaries between difficulty levels (0.0 to 1.0, must be ascending)">
              <InfoCircleOutlined style={{ marginLeft: 4 }} />
            </Tooltip>
            {errors.thresholds && <Typography.Text type="danger" style={{ marginLeft: 8 }}>{errors.thresholds}</Typography.Text>}
            <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
              {config.thresholds.map((threshold, index) => (
                <Col key={index} span={8}>
                  <InputNumber
                    min={0}
                    max={1}
                    step={0.01}
                    precision={2}
                    value={threshold}
                    onChange={(value) => handleThresholdChange(index, value)}
                    style={{ width: "100%" }}
                    addonBefore={`${index + 1}:`}
                  />
                </Col>
              ))}
            </Row>
          </div>

          {/* Action Buttons */}
          <Row gutter={16}>
            <Col>
              <Button type="primary" onClick={handleSave} disabled={hasErrors}>
                Save Configuration
              </Button>
            </Col>
            <Col>
              <Button onClick={handleReset}>
                Reset to Default
              </Button>
            </Col>
          </Row>

          {hasErrors && (
            <Typography.Text type="danger">
              Please fix the validation errors above before saving.
            </Typography.Text>
          )}
        </Space>
      </PermissionOverlay>
    </Card>
  );
}; 
